import { isAuthError, redirectToLogin, attemptSessionRefresh, validateSession } from './sessionValidationUtil';

/**
 * API Error Handler utility for centralized error handling across all services
 * Provides consistent error handling, retry logic, and auth error detection
 */

interface ErrorHandlingOptions {
    /** Should retry the operation after refreshing session if auth error is detected */
    retryOnAuthError?: boolean;
    /** Should redirect to login if auth error is detected and retry fails */
    redirectOnAuthError?: boolean;
    /** Should log errors to console */
    logErrors?: boolean;
    /** Context identifier for better error logging */
    context?: string;
}

const DEFAULT_OPTIONS: ErrorHandlingOptions = {
    retryOnAuthError: true,
    redirectOnAuthError: true,
    logErrors: true,
};

/**
 * Wrap an async operation with comprehensive error handling
 * @param operation - The async function to execute
 * @param context - Context identifier (e.g., 'ServiceName.methodName')
 * @param options - Error handling options
 * @returns Result of the operation
 */
export async function withErrorHandling<T>(
    operation: () => Promise<T>,
    context: string,
    options: ErrorHandlingOptions = {}
): Promise<T> {
    const opts = { ...DEFAULT_OPTIONS, ...options };

    try {
        // Execute the operation
        const result = await operation();
        return result;

    } catch (error) {
        if (opts.logErrors) {
            console.error(`❌ Error in ${context}:`, error);
        }

        // Check if this is an auth error
        if (isAuthError(error)) {
            return await handleAuthError(error, context, operation, opts);
        }

        // Check if this is a network error
        if (isNetworkError(error)) {
            handleNetworkError(error, context);
        }

        // Check if this is a database error
        if (isDatabaseError(error)) {
            handleDatabaseError(error, context);
        }

        // Re-throw the error for the caller to handle
        throw error;
    }
}

/**
 * Handle authentication-related errors
 * @param error - The auth error
 * @param context - Context identifier
 * @param operation - The operation to retry
 * @param options - Error handling options
 */
async function handleAuthError<T>(
    error: any,
    context: string,
    operation: () => Promise<T>,
    options: ErrorHandlingOptions
): Promise<T> {
    console.warn(`⚠️ Auth error detected in ${context}`);

    // Try to refresh the session if enabled
    if (options.retryOnAuthError) {
        console.log(`🔄 Attempting session refresh for ${context}...`);

        const refreshSuccess = await attemptSessionRefresh();

        if (refreshSuccess) {
            console.log(`✅ Session refreshed, retrying ${context}...`);

            try {
                // Retry the operation with refreshed session
                const result = await operation();
                console.log(`✅ Retry successful for ${context}`);
                return result;
            } catch (retryError) {
                console.error(`❌ Retry failed for ${context}:`, retryError);

                // If retry also fails with auth error, redirect to login
                if (isAuthError(retryError) && options.redirectOnAuthError) {
                    redirectToLogin(`Auth error in ${context} after retry`, window.location.pathname);
                }

                throw retryError;
            }
        } else {
            // Session refresh failed
            console.error(`❌ Session refresh failed for ${context}`);

            if (options.redirectOnAuthError) {
                redirectToLogin(`Session refresh failed in ${context}`, window.location.pathname);
            }
        }
    } else if (options.redirectOnAuthError) {
        // No retry, just redirect
        redirectToLogin(`Auth error in ${context}`, window.location.pathname);
    }

    throw error;
}

/**
 * Check if error is a network error
 */
function isNetworkError(error: any): boolean {
    if (!error) return false;

    const errorString = String(error).toLowerCase();
    const errorMessage = error?.message?.toLowerCase() || '';

    const networkPatterns = [
        'network',
        'fetch',
        'cors',
        'timeout',
        'aborted',
        'connection',
        'econnrefused',
        'enotfound',
    ];

    return networkPatterns.some(
        pattern => errorString.includes(pattern) || errorMessage.includes(pattern)
    );
}

/**
 * Handle network errors
 */
function handleNetworkError(error: any, context: string): void {
    console.error(`🌐 Network error in ${context}:`, error.message || error);

    // Could add retry logic, offline detection, etc.
    // For now, just log it
}

/**
 * Check if error is a database error
 */
function isDatabaseError(error: any): boolean {
    if (!error) return false;

    const errorCode = error?.code?.toUpperCase() || '';

    // PostgreSQL error codes (PGRST prefix for PostgREST)
    const dbErrorPatterns = [
        'PGRST',
        '23505', // unique violation
        '23503', // foreign key violation
        '23502', // not null violation
        '42P01', // undefined table
        '42703', // undefined column
    ];

    return dbErrorPatterns.some(pattern => errorCode.includes(pattern));
}

/**
 * Handle database errors
 */
function handleDatabaseError(error: any, context: string): void {
    const errorCode = error?.code || '';
    const errorMessage = error?.message || '';

    console.error(`🗄️ Database error in ${context}:`, {
        code: errorCode,
        message: errorMessage,
    });

    // Could add specific handling for different error codes
    // For now, just log it
}

/**
 * Validate session before executing operation
 * Useful for wrapping operations that require authentication
 */
export async function withSessionValidation<T>(
    operation: () => Promise<T>,
    context: string,
    options?: { skipValidationOnTransition?: boolean }
): Promise<T> {
    // Import dynamically to avoid circular dependency
    const { isAuthInTransition, waitForAuthTransition } = await import('./sessionValidationUtil');
    
    // If auth is in transition (e.g., during SSO callback), wait for it
    if (isAuthInTransition()) {
        console.log(`⏳ ${context}: Auth in transition, waiting...`);
        await waitForAuthTransition(8000);
        
        // If still in transition and skipValidationOnTransition is true, proceed anyway
        if (isAuthInTransition() && options?.skipValidationOnTransition) {
            console.log(`⚠️ ${context}: Auth still in transition, proceeding without validation`);
            return withErrorHandling(operation, context);
        }
    }
    
    // Validate session first
    const { valid, reason } = await validateSession();

    if (!valid) {
        // Before failing, try one refresh attempt
        console.log(`⚠️ ${context}: Session invalid (${reason}), attempting refresh...`);
        const refreshSuccess = await attemptSessionRefresh();
        
        if (refreshSuccess) {
            console.log(`✅ ${context}: Session refreshed, proceeding with operation`);
            return withErrorHandling(operation, context);
        }
        
        const error = new Error(`Session invalid: ${reason || 'Unknown reason'}`);
        console.error(`❌ Session validation failed in ${context}:`, error.message);
        throw error;
    }

    // Session is valid, proceed with operation wrapped in error handling
    return withErrorHandling(operation, context);
}

/**
 * Create a wrapper function that automatically handles errors
 * Useful for creating service methods
 */
export function createErrorHandledMethod<TArgs extends any[], TResult>(
    method: (...args: TArgs) => Promise<TResult>,
    context: string,
    options?: ErrorHandlingOptions
): (...args: TArgs) => Promise<TResult> {
    return async (...args: TArgs): Promise<TResult> => {
        return withErrorHandling(() => method(...args), context, options);
    };
}

/**
 * Log error details for debugging
 */
export function logError(error: any, context: string): void {
    console.group(`❌ Error Details: ${context}`);
    console.error('Error:', error);
    console.error('Message:', error?.message);
    console.error('Code:', error?.code);
    console.error('Status:', error?.status || error?.statusCode);
    console.error('Stack:', error?.stack);
    console.groupEnd();
}
